// Admin Boats list (ADR 0165) — the fleet-wide view of every boat, with the
// lifecycle actions an operator needs: decommission (hide from the owners),
// restore (give it back), and hard delete (erase everything).
//
// Each boat's name carries a link to its notebook (TOM-616), which asks for a
// reason before it opens.
//
// The state column is the point of the screen, so its precedence is explicit:
// deregistered outranks decommissioned, because a deregistered boat cannot
// authenticate at all and restoring it would NOT bring it back — the two
// states can coexist on one row and showing only the softer one would mislead.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { BoatAdminActions } from '../components/BoatAdminActions';
import { StatusBadge } from '../components/StatusBadge';

interface AdminBoatRow {
  id: string;
  display_name: string;
  created_at: string;
  last_contacted_at: string | null;
  core_version: string | null;
  deregistered_at: string | null;
  decommissioned_at: string | null;
  owner_emails: string[];
}

function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString();
}

// Colour comes from the shared admin badge vocabulary: red for the terminal
// state, amber for the reversible one, teal for healthy.
function stateBadge(b: AdminBoatRow): { status: string; label: string } {
  if (b.deregistered_at !== null) return { status: 'rejected', label: 'Deregistered' };
  if (b.decommissioned_at !== null) return { status: 'suggested', label: 'Decommissioned' };
  return { status: 'published', label: 'Active' };
}

export function AdminBoats() {
  const [boats, setBoats] = useState<AdminBoatRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await adminApi<{ boats: AdminBoatRow[] }>('/v1/admin/boats');
      setBoats(res.boats);
      setError(null);
    } catch (err) {
      const e = err as { body?: { message?: string } };
      setError(e.body?.message ?? 'Failed to load boats.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Boats</h1>
        <span className="admin-count">{boats === null ? '…' : `${boats.length} total`}</span>
      </header>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}

      {boats === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {boats !== null ? (
        <div className="scroll-x">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Owners</th>
                <th>Created</th>
                <th>Last contact</th>
                <th>Core</th>
                <th>State</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {boats.map((b) => {
                const badge = stateBadge(b);
                return (
                  <tr key={b.id} data-testid={`admin-boat-row-${b.id}`}>
                    <td>
                      {b.display_name}
                      <Link
                        className="notebook-link"
                        to={`/admin/boats/${b.id}/notebook`}
                        state={{
                          boatName: b.display_name,
                          from: { to: '/admin/boats', label: 'Boats' },
                        }}
                        aria-label={`Notebook for ${b.display_name}`}
                        data-testid={`admin-boat-notebook-${b.id}`}
                      >
                        Notebook
                      </Link>
                    </td>
                    <td>{b.owner_emails.length === 0 ? '—' : b.owner_emails.join(', ')}</td>
                    <td>{formatDate(b.created_at)}</td>
                    <td>{formatDate(b.last_contacted_at)}</td>
                    <td>{b.core_version ?? '—'}</td>
                    <td>
                      <StatusBadge status={badge.status} label={badge.label} />
                    </td>
                    <td>
                      <BoatAdminActions
                        boatId={b.id}
                        boatName={b.display_name}
                        decommissioned={b.decommissioned_at !== null}
                        onDone={() => void load()}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
